import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchBranches, deleteBranch } from '../api';
import BranchCreateAndViewDetails from './BranchCreateAndViewDetails';
import { BranchCard } from '../components';
import { usePermissions } from '../../../hooks/usePermissions';
import { 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined, 
  PlusOutlined,
  SearchOutlined
} from "@ant-design/icons";
import { 
  Table, 
  Input, 
  Button, 
  Space, 
  Typography, 
  Spin, 
  Pagination, 
  Grid,
  Card,
  Row,
  Col,
  Tag,
  Modal,
  message,
  Tooltip,
  Popconfirm
} from "antd";
import "bootstrap/dist/css/bootstrap.min.css";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const BranchesList = () => {
  const [branches, setBranches] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [modalState, setModalState] = useState({
    visible: false,
    mode: 'create', // 'create', 'view', 'edit'
    branchData: null
  });

  const [messageApi, contextHolder] = message.useMessage();

  const navigate = useNavigate();
  const { canViewModule, canViewCreateModule, canEditModule, can, canViewField } = usePermissions();

  const canViewNameField = canViewField('branches', 'name');
  const canViewCodeField = canViewField('branches', 'code');
  const canViewAddressField = canViewField('branches', 'address');
  const canViewManagerField = canViewField('branches', 'manager');
  const canViewStatusField = canViewField('branches', 'status');
  const canViewContactField = canViewField('branches', 'contactDetails');
  const screens = useBreakpoint();

  useEffect(() => {
    loadBranches();
  }, [pagination.current, pagination.pageSize, searchQuery]);

  const loadBranches = async () => {
    setLoading(true);
    try {
      const response = await fetchBranches({
        search: searchQuery,
        page: pagination.current,
        limit: pagination.pageSize,
      });
      setBranches(response.data || []);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination?.total || response.meta?.total || 0,
      }));
    } catch (err) {
      console.error('Failed to fetch branches', err);
      messageApi.error('Failed to load branches. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchQuery(searchTerm);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize,
    }));
  };

  const handleView = (branch) => {
    setModalState({
      visible: true,
      mode: 'view',
      branchData: branch
    });
  };

  const handleEdit = (branch) => {
    setModalState({
      visible: true,
      mode: 'edit',
      branchData: branch
    });
  };

  const handleCreate = () => {
    setModalState({
      visible: true,
      mode: 'create',
      branchData: null
    });
  };

  const handleDelete = async (branch) => {
    console.log('Attempting to delete branch:', branch._id);
    try {
      const response = await deleteBranch(branch._id);
      console.log('Delete response:', response);
      messageApi.success('Branch deleted successfully');
      loadBranches();
    } catch (error) {
      console.error('Error deleting branch:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      let errorMessage = 'Failed to delete branch. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      messageApi.error(errorMessage);
    }
  };

  const handleModalClose = () => {
    setModalState({
      visible: false,
      mode: 'create',
      branchData: null
    });
  };

  const handleSuccess = () => {
    loadBranches();
    handleModalClose();
  };

  const handleModeChange = (newMode, branchData) => {
    setModalState({
      visible: true,
      mode: newMode,
      branchData: branchData
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'green';
      case 'inactive': return 'orange';
      case 'under_maintenance': return 'blue';
      case 'closed': return 'red';
      default: return 'default';
    }
  };

  const columns = [
    canViewNameField && {
      title: 'Branch Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      responsive: ['xs', 'sm', 'md', 'lg'],
    },
    canViewCodeField && {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      responsive: ['sm', 'md', 'lg'],
    },
    canViewAddressField && {
      title: 'Location',
      key: 'location',
      render: (_, record) => {
        const address = record.address;
        if (!address) return '-';
        return `${address.city || ''}, ${address.state || ''}`.replace(/^,\s*|,\s*$/, '') || '-';
      },
      responsive: ['md', 'lg'],
    },
    canViewManagerField && {
      title: 'Manager',
      dataIndex: ['manager', 'name'],
      key: 'manager',
      render: (managerName) => managerName || '-',
      responsive: ['lg'],
    },
    canViewStatusField && {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} style={{ textTransform: 'capitalize' }}>
          {status}
        </Tag>
      ),
      responsive: ['sm', 'md', 'lg'],
    },
    canViewContactField && {
      title: 'Contact',
      dataIndex: ['contactDetails', 'phone'],
      key: 'contact',
      render: (phone) => phone || '-',
      responsive: ['lg'],
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          {canViewModule('branches') && (
            <Tooltip title="View Details">
              <Button
                type="text"
                icon={<EyeOutlined />}
                size="small"
                onClick={() => handleView(record)}
              />
            </Tooltip>
          )}
          {canEditModule('branches') && (
            <Tooltip title="Edit Branch">
              <Button
                type="text"
                icon={<EditOutlined />}
                size="small"
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
          )}
          {can('branches', 'delete') && (
            <Popconfirm
              title="Delete Branch"
              description={`Are you sure you want to delete "${record.name}"? This action cannot be undone.`}
              onConfirm={() => handleDelete(record)}
              onCancel={() => console.log('Delete cancelled')}
              okText="Yes, Delete"
              cancelText="Cancel"
              okType="danger"
            >
              <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                />
            </Popconfirm>
          )}
        </Space>
      ),
      responsive: ['xs', 'sm', 'md', 'lg'],
    },
  ].filter(Boolean);

  // Mobile card view for small screens
  const renderMobileCard = (branch) => (
    <BranchCard
      key={branch._id}
      branch={branch}
      actions={[
        canViewModule('branches') && (
          <EyeOutlined key="view" onClick={() => handleView(branch)} />
        ),
        canEditModule('branches') && (
          <EditOutlined key="edit" onClick={() => handleEdit(branch)} />
        ),
        can('branches', 'delete') && (
          <Popconfirm
            key="delete"
            title="Delete Branch"
            description={`Are you sure you want to delete "${branch.name}"?`}
            onConfirm={() => handleDelete(branch)}
            onCancel={() => console.log('Delete cancelled')}
            okText="Yes, Delete"
            cancelText="Cancel"
            okType="danger"
          >
            <DeleteOutlined />
          </Popconfirm>
        ),
      ].filter(Boolean)}
    />
  );

  if (!canViewModule('branches')) {
    return (
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <Card>
              <div className="text-center">
                <Title level={3}>Access Denied</Title>
                <Text>You don't have permission to view branches.</Text>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {contextHolder}
      <div className="row">
        <div className="col-12">
          <Card>
            <Row justify="space-between" align="middle" className="mb-4">
              <Col>
                <Title level={2} style={{ margin: 0 }}>
                  Branches Management
                </Title>
              </Col>
              {canViewCreateModule('branches') && (
                <Col>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                    size={screens.xs ? 'small' : 'middle'}
                  >
                    {screens.xs ? 'Add' : 'Add Branch'}
                  </Button>
                </Col>
              )}
            </Row>

            {/* Search Section */}
            <Row className="mb-3">
              <Col xs={24} sm={18} md={20}>
                <Input.Search
                  placeholder="Search branches by name, code, or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onSearch={handleSearch}
                  enterButton={<SearchOutlined />}
                  size={screens.xs ? 'small' : 'middle'}
                />
              </Col>
            </Row>

            {/* Loading Spinner */}
            <Spin spinning={loading}>
              {/* Desktop Table View */}
              {screens.md ? (
                <Table
                  columns={columns}
                  dataSource={branches}
                  rowKey="_id"
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  size={screens.lg ? 'middle' : 'small'}
                />
              ) : (
                /* Mobile Card View */
                <div>
                  {branches.map(renderMobileCard)}
                </div>
              )}
            </Spin>

            {/* Pagination */}
            {pagination.total > 0 && (
              <Row justify="center" className="mt-4">
                <Col>
                  <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={handleTableChange}
                    showSizeChanger
                    showQuickJumper={!screens.xs}
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} of ${total} branches`
                    }
                    size={screens.xs ? 'small' : 'default'}
                    responsive
                  />
                </Col>
              </Row>
            )}
          </Card>
        </div>
      </div>

      {/* Create/Edit/View Modal */}
      <BranchCreateAndViewDetails
        visible={modalState.visible}
        mode={modalState.mode}
        branchData={modalState.branchData}
        onClose={handleModalClose}
        onSuccess={handleSuccess}
        onModeChange={handleModeChange}
      />
    </div>
  );
};

export default BranchesList;
